import type { IGameLevel, StatDefinition } from './IGameLevel';
import type { IBeingState } from '../controllers/states/being/IBeingState';
import { FleeingState } from '../controllers/states/being/FleeingState';
import { ExploringState } from '../controllers/states/being/ExploringState';
import { EatingState } from '../controllers/states/being/EatingState';
import { RestingState } from '../controllers/states/being/RestingState';
import { ToolType } from '../models/WorldModel';
import type { Being } from '../models/Being';
import type { Beast } from '../models/Beast';
import type { PubSub } from '../PubSub';
import type { GameEvents } from '../events';
import type { ConsoleController } from '../controllers/ConsoleController';
import { level1Intro, level1Complete } from '../assets/text';
import { DeadState } from '../controllers/states/being/DeadState';
import tips from '../assets/text/level1-tips.json';
import { SocialCohesionService } from '../services/SocialCohesionService';
import { LEVEL1 } from '../models/constants';

export class Level1 implements IGameLevel {
    readonly id = 'level-1';

    readonly tools: ToolType[] = [
        ToolType.Raise,
        ToolType.Lower,
        ToolType.Flatten,
    ];

    readonly beingStates: Map<string, IBeingState> = new Map([
        [DeadState.stateName,     new DeadState()],
        [FleeingState.stateName,  new FleeingState()],
        [ExploringState.stateName, new ExploringState()],
        [EatingState.stateName,   new EatingState()],
        [RestingState.stateName,  new RestingState()],
    ]);

    private readonly cohesionService: SocialCohesionService;
    private isComplete = false;
    private consoleController!: ConsoleController;
    private pubsub!: PubSub<GameEvents>;
    private shownTipIds = new Set<string>();
    private ticksSinceLastTip = 0;

    readonly stats: StatDefinition[] = [
        {
            label: 'SOCIAL COHESION',
            getValue: () => `${Math.min(this.cohesionService.score, this.cohesionService.target)}/${this.cohesionService.target}`,
            getFraction: () => Math.min(1, this.cohesionService.score / this.cohesionService.target),
        },
    ];

    constructor({ consoleController, pubsub, cohesionService }: { consoleController: ConsoleController; pubsub: PubSub<GameEvents>; cohesionService: SocialCohesionService }) {
        this.consoleController = consoleController;
        this.pubsub = pubsub;
        this.cohesionService = cohesionService;
    }

    begin(): void {
        this.consoleController.play(level1Intro);
    }

    tick(beings: Being[], _beasts: Beast[]): void {
        this.cohesionService.tick(beings);

        if (!this.isComplete && this.cohesionService.isComplete) {
            this.isComplete = true;
            this.consoleController.play(level1Complete);
            this.pubsub.publish('level:complete', this.id);
        }

        this.ticksSinceLastTip++;
        if (this.ticksSinceLastTip >= LEVEL1.TIP_INTERVAL_TICKS) {
            const unseen = tips.filter(t => !this.shownTipIds.has(t.id));
            if (unseen.length > 0) {
                const tip = unseen[Math.floor(Math.random() * unseen.length)];
                this.shownTipIds.add(tip.id);
                this.pubsub.publish('level:tip', tip.text);
            }
            this.ticksSinceLastTip = 0;
        }
    }
}
